define([
  'react',
  'prop-types',
  'react-redux',
  'immutable',
  'underscore',
  'color',
  'es6!sumo/util',
  'es6!sumo/util/Font',
  'es6!launcher/actions/ListBuilderAppActions',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/wysiwyg/dom',
  'es6!launcher/util/listbuilder/wysiwyg/snap',
  'es6!launcher/views/listbuilder/wysiwyg/components/index',
  'jsx!launcher/views/listbuilder/wysiwyg/components/SumoBadge',
  'jsx!launcher/views/listbuilder/wysiwyg/PopupScale',
  'jsx!launcher/views/listbuilder/wysiwyg/PopupAnimation',
  'jsx!launcher/views/listbuilder/wysiwyg/PopupMeasurement',
  'es6!launcher/util/listbuilder/constants',
  'es6!launcher/util/listbuilder/stylebuffer/smartbar',
  'jsx!launcher/views/listbuilder/components/BrandingLogoSvg',
], (
  React,
  PropTypes,
  ReactRedux,
  Immutable,
  _,
  color,
  Util,
  FontUtil,
  AppActions,
  WysiwygUtil,
  WysiwygDom,
  WysiwygSnap,
  WysiwygComponents,
  SumoBadge,
  PopupScale,
  PopupAnimation,
  PopupMeasurement,
  Constants,
  SmartBarBuffer,
  BrandingLogoSvg,
) => {
  const POPUP_CLASS_NAME = 'sumome-react-wysiwyg-popup-container';
  const ESC = 27;
  const { classNames, addEvent, getQueryVariables } = Util;
  const { FEATURES } = Util.Constants;
  const { FormTypes } = Constants;
  const {
    getRoundedPosition,
    styleToPosition,
  } = WysiwygUtil;
  const { getSnapFunction } = WysiwygSnap;
  const {
    IsoLogoColor,
  } = BrandingLogoSvg;

  const typeToComponent = {
    text: WysiwygComponents.Text,
    html: WysiwygComponents.Html,
    image: WysiwygComponents.Image,
    // Background renders the same as Image
    background: WysiwygComponents.Image,
    button: WysiwygComponents.Button,
    textField: WysiwygComponents.TextField,
    checkbox: WysiwygComponents.Checkbox,
    dropdown: WysiwygComponents.Dropdown,
    radio: WysiwygComponents.Radio,
    checkboxGroup: WysiwygComponents.CheckboxGroup,
    hidden: WysiwygComponents.Hidden,
    countdown: WysiwygComponents.Countdown,
    socialButton: WysiwygComponents.SocialButton,
    gridRow: WysiwygComponents.GridRow,
    gridColumn: WysiwygComponents.GridColumn,
    scaledStage: WysiwygComponents.ScaledStage,
    tab: WysiwygComponents.Tab,
    discount: WysiwygComponents.Discount,
    game: WysiwygComponents.Game,
    progressBar: WysiwygComponents.ProgressBar,
    socialProofSVG: WysiwygComponents.SocialProofSVG.default,
  };

  function componentForType(type) {
    return typeToComponent[type];
  }

  function getOrderedComponentIds(hoveredComponentIds, components) {
    const componentIndexes = [];
    const allComponents = WysiwygDom.getDescendantComponents(components);

    // Store indexes to determine which hovered component is on top.
    allComponents.forEach((component, index) => {
      const componentId = component.get('id');
      if (hoveredComponentIds[componentId]) {
        componentIndexes.push({ id: componentId, index });
      }
    });

    if (!componentIndexes.length) return null;
    const sortedComponentIndexes = _.sortBy(componentIndexes, 'index');

    return sortedComponentIndexes.map(i => i.id);
  }

  // Finds the top most component from the hovered ids.
  function findHoveredComponentId(hoveredComponentIds, components) {
    const componentIndexes = [];
    if (!hoveredComponentIds || !components) return null;

    const allComponents = WysiwygDom.getDescendantComponents(components);

    // Store indexes to determine which hovered component is on top.
    allComponents.forEach((component, index) => {
      const componentId = component.get('id');
      if (hoveredComponentIds[componentId]) {
        componentIndexes.push({ id: componentId, index });
      }
    });

    if (!componentIndexes.length) return null;
    const sortedComponentIndexes = _.sortBy(componentIndexes, 'index');

    // Get the last, and highest index in the array since it is the top component.
    return sortedComponentIndexes[sortedComponentIndexes.length - 1].id;
  }

  // Standalone breakpoint utility functions
  /**
   * Gets breakpoint keys from breakpoints data structure
   * @param {*} breakpointSizes - Breakpoints data (Immutable Map or plain object)
   * @returns {Array<string>} - Array of breakpoint keys
   */
  function getBreakpointKeys(breakpointSizes) {
    if (typeof breakpointSizes.get === 'function') {
      // Handle Immutable Map
      return breakpointSizes.keySeq().toArray();
    }
    // Handle plain object
    return Object.keys(breakpointSizes);
  }

  /**
   * Extracts breakpoint styles from breakpoints data structure
   * @param {*} breakpoints - Breakpoints data (Immutable Map or plain object)
   * @param {string} breakpointKey - The breakpoint key (e.g., '480', '768')
   * @returns {Immutable.Map} - Immutable Map of breakpoint styles
   */
  function extractBreakpointStyles(breakpoints, breakpointKey) {
    if (typeof breakpoints.get === 'function') {
      // Handle Immutable breakpoints
      const breakpointData = breakpoints.get(breakpointKey, Immutable.Map());
      const styles = breakpointData.get('style', Immutable.Map());
      return styles;
    }
    if (typeof breakpoints === 'object' && !breakpoints.get) {
      // Handle plain object breakpoints
      const breakpointData = breakpoints[breakpointKey] || {};
      const styles = breakpointData.style || {};
      return Immutable.fromJS(styles);
    }
    return Immutable.Map();
  }

  /**
   * Recursively composes styles by applying breakpoints in descending order
   * @param {Immutable.Map} currentBaseStyle - Current base style to build upon
   * @param {*} breakpoints - Breakpoints data
   * @param {Array<number>} remainingBreakpoints - Remaining breakpoints to process
   * @param {number} selectedSize - The selected breakpoint size
   * @returns {Immutable.Map} - Composed styles object
   */
  function composeStylesRecursively(
    currentBaseStyle,
    breakpoints,
    remainingBreakpoints,
    selectedSize,
  ) {
    // Base case: no more breakpoints to process
    if (remainingBreakpoints.length === 0) {
      return currentBaseStyle;
    }

    const [currentBreakpoint, ...restBreakpoints] = remainingBreakpoints;

    // Only process breakpoints that are >= selectedSize (we're going from largest to smallest)
    if (currentBreakpoint < selectedSize) {
      return currentBaseStyle;
    }

    // Get styles for current breakpoint
    const breakpointKey = currentBreakpoint.toString();
    const breakpointStyles = extractBreakpointStyles(breakpoints, breakpointKey);

    // Merge current breakpoint styles with current base style using Immutable
    const newBaseStyle = currentBaseStyle.mergeDeep(breakpointStyles);

    // Recursively process remaining breakpoints
    return composeStylesRecursively(
      newBaseStyle,
      breakpoints,
      restBreakpoints,
      selectedSize,
    );
  }

  /**
   * Composes final styles by applying breakpoint overrides to base styles
   * @param {string} selectedSize - The selected breakpoint size (e.g., '480', '768', '1024')
   * @param {Object} componentJSON - The component configuration object
   * @returns {Object|null} - The final composed styles or null if no overrides needed
   */
  function composeBreakpointStyles(selectedSize, componentJSON) {
    const breakpoints = componentJSON?.breakpoints;
    if (!breakpoints) return null;

    // Get all breakpoint keys and sort them in descending order (largest to smallest)
    const breakpointKeys = getBreakpointKeys(breakpoints);
    const sortedBreakpoints = breakpointKeys
      .map((key) => parseInt(key, 10))
      .sort((a, b) => b - a); // Descending: [1024, 768, 480]

    // Start with base styles as the initial "baseStyle"
    const baseStyles = componentJSON.style || Immutable.Map();

    // Recursively compose styles from largest breakpoint down to selected size
    const composedStyles = composeStylesRecursively(
      baseStyles,
      breakpoints,
      sortedBreakpoints,
      selectedSize,
    );
    return composedStyles;
  }

  /**
   * Gets the responsive breakpoint size based on current window width
   * @param {*} breakpointSizes - Breakpoints data (Immutable Map or plain object)
   * @returns {string|undefined} - The breakpoint key that matches current window width
   */
  function getResponsiveSize(breakpointSizes) {
    const width = window.innerWidth
      || document.documentElement.clientWidth
      || document.body.clientWidth;
    // Handle both Immutable Maps and plain objects
    const breakpointKeys = getBreakpointKeys(breakpointSizes);

    // The breakpoint keys are numeric values (e.g., "480", "768", "1024")
    // We need to compare the key itself (as a number) with the window width
    // Mobile-first: apply breakpoint when width is >= breakpoint
    const breakpointSize = breakpointKeys.find((key) => width <= parseInt(key, 10));
    return breakpointSize;
  }

  @PopupMeasurement
  @PopupScale
  @PopupAnimation
  class Popup extends React.Component {
    constructor(props) {
      super(props);
      this.state = _.extend(this.buildState(props), {
        draggingComponentId: null,
        focusedFieldComponentId: null,
        activeDropTargetComponentId: null,
        barTop: 0,
      });

      this.hoveredComponentIds = {};

      this.onMouseEnterComponent = this.onMouseEnterComponent.bind(this);
      this.onMouseLeaveComponent = this.onMouseLeaveComponent.bind(this);
      this.onSelectComponent = this.onSelectComponent.bind(this);
      this.onUnselectComponent = this.onUnselectComponent.bind(this);
      this.onDragComponent = this.onDragComponent.bind(this);
      this.onResizeComponent = this.onResizeComponent.bind(this);
      this.onFocusFieldComponent = this.onFocusFieldComponent.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.getSnappedPosition = this.getSnappedPosition.bind(this);
      this.renderComponent = this.renderComponent.bind(this);

      this.form = null;
      this.firstErrorFound = false;

      this.scrollHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );

      this.queryVars = getQueryVariables();
    }

    componentDidMount() {
      const {
        goal,
        advancedForm,
        onClickAction,
        position,
        showType,
        isPreview,
        formType,
        updateSetting,
        api,
      } = this.props;

      if (api) {
        const siteId = api.properties.siteId;
        const sumoUrl = api.properties.sumoUrl;
        updateSetting('siteId', siteId);
        updateSetting('sumoUrl', sumoUrl);
      }

      document.addEventListener('keyup', this.onKeyUp);

      if (goal === 'custom' && advancedForm && this.advancedForm) {
        const closeButtons = this.advancedForm.querySelectorAll('.sumome-advanced-form-close');
        for (let i = 0; i < closeButtons.length; i += 1) {
          addEvent(closeButtons[i], 'click', () => onClickAction('close'));
        }
        // Run facebook messenger script when popup is loaded from script in advanced_form snippet
        if (advancedForm.indexOf('<script>runFacebookSumo();</script>') !== -1) {
          const facebookCodeTag = document.createElement('script');
          facebookCodeTag.type = 'text/javascript';
          facebookCodeTag.text = 'runFacebookSumo();';
          document.body.appendChild(facebookCodeTag);
        }
      }

      if (formType === FormTypes.SMART_BAR) {
        const barHeight = this.form ? this.form.offsetHeight : this.advancedForm.offsetHeight;
        const barWidth = this.form ? this.form.offsetWidth : this.advancedForm.offsetWidth;

        // Style Buffer
        if (!isPreview) {
          SmartBarBuffer.styleBuffer(this.form || this.advancedForm, { position, showType });
        }

        if (position === 'top' && showType === 'static' && this.form) {
          // Check if static bar is out of place.
          // Sometimes sites have body styles that screw with it.
          this.form.style.top = `-${this.form.offsetHeight}px`;
        }

        this.setState({ barHeight, barWidth });
      }
    }

    componentWillReceiveProps(nextProps) {
      this.setState(this.buildState(nextProps));
    }

    componentDidUpdate(prevProps) {
      const {
        formType,
        scaleRatio,
        isPreview,
        width,
        height,
        onChangePopupSize,
        onChangeSmartBarHeight,
      } = this.props;

      // Check if smartbar height has changed
      if (formType === FormTypes.SMART_BAR && isPreview) {
        const barHeight = this.form ? this.form.offsetHeight : this.advancedForm.offsetHeight;
        if (prevProps.barHeight !== barHeight && typeof onChangeSmartBarHeight === 'function') {
          onChangeSmartBarHeight(barHeight);
        }
      }

      // Check if scale ratio has changed
      if (
        prevProps.scaleRatio !== this.state.scaleRatio
        || prevProps.height !== this.state.height
        || prevProps.width !== this.state.width
        || prevProps.scaleRatio !== scaleRatio
        || prevProps.height !== height
        || prevProps.width !== width
      ) {
        this.setState({ scaleRatio, height, width });
        if (typeof onChangePopupSize === 'function') {
          onChangePopupSize(width, height, scaleRatio);
        }
      }
    }

    componentWillUnmount() {
      const { position, showType, isPreview, formType } = this.props;
      document.removeEventListener('keyup', this.onKeyUp);

      if (formType === FormTypes.SMART_BAR) {
        if (!isPreview) {
          SmartBarBuffer.styleBuffer(false, { position, showType });
        }
      }
    }

    // We collect all ids the mouse is currently over and then select the
    // hovered component from those by getting the highest index.
    onMouseEnterComponent(id) {
      const {
        isPreview,
        components,
      } = this.props;
      const { draggingComponentId, hoveredComponentId } = this.state;
      if (!isPreview) return;

      this.hoveredComponentIds[id] = true;

      if (!draggingComponentId) {
        const newHoveredId = findHoveredComponentId(this.hoveredComponentIds, components);
        if (hoveredComponentId !== newHoveredId) {
          this.setState({ hoveredComponentId: newHoveredId });
        }
      }

      const activeDropTargetComponent = this.getActiveDropTargetComponent();

      this.setState({ activeDropTargetComponent });
    }

    // Removes hovered ids and recalculates the hovered component.
    onMouseLeaveComponent(id) {
      const {
        isPreview,
        components,
      } = this.props;
      const { hoveredComponentId } = this.state;
      if (!isPreview) return;

      delete this.hoveredComponentIds[id];

      const newHoveredId = findHoveredComponentId(this.hoveredComponentIds, components);

      if (hoveredComponentId !== newHoveredId) {
        this.setState({ hoveredComponentId: newHoveredId });
      }

      const activeDropTargetComponent = this.getActiveDropTargetComponent();

      this.setState({ activeDropTargetComponent });
    }

    onSelectComponent(selectedComponentId) {
      const id = selectedComponentId;
      const { onSelectComponent, components } = this.props;
      const { hoveredComponentId } = this.state;
      const selectedComponent = components && id && WysiwygDom
        ? WysiwygDom.getComponentWithId(components, id)
        : null;
      const type = selectedComponent?.get('type');

      // can't touch this
      if (type === 'gridRow' || type === 'gridColumn' || type === 'scaledStage') {
        return;
      }

      const newState = {
        selectedComponentId: id,
        activeDropTargetComponentId: null,
      };

      // If the selected component was the hovered component, then unhover.
      if (id === hoveredComponentId) {
        newState.hoveredComponentId = null;
      }

      this.setState(newState);
      if (onSelectComponent) onSelectComponent(id);
    }

    onUnselectComponent(e) {
      const { onSelectComponent } = this.props;
      const targetClassName = e.target.className || '';

      if (typeof targetClassName === 'string' && targetClassName.indexOf(POPUP_CLASS_NAME) < 0) return;
      if (onSelectComponent) onSelectComponent(null);
    }

    // This is used for both move and resize dragging. Set which component is moving/resizing
    // and unselect component if not the moving/resizing component. Purely used to check if
    // a component is moving or resizing.
    onDragComponent(id, dragEvent) {
      const { onChangePopupJson, formType, scaleRatio } = this.props;
      let { components } = this.props;
      const dropResult = {
        isValidMove: true,
        isNewParent: false,
      };
      const { selectedComponentId, activeDropTargetComponent, draggingComponentId } = this.state;
      const newState = { draggingComponentId: id, draggingComponentParent: undefined };

      if (selectedComponentId != id) newState.selectedComponentId = null;

      // fix edge case where onMouseEnter isn't fired for components when already hovered
      if (_.isEmpty(this.hoveredComponentIds) && formType === FormTypes.WELCOME_MAT && id !== null) {
        const componentLineage = WysiwygDom.getComponentLineage(components, id);

        // add every component in the drag components lineage as a hovered component
        componentLineage.slice(0, -1).forEach((component) => {
          this.hoveredComponentIds[component.get('id')] = true;
        });
      }

      // if we're dragging a component (draggingComponentId) and are able to drop the compnent into
      // a good drop target (activeDropTargetComponent), then rework the structure and positioning
      if (draggingComponentId && activeDropTargetComponent) {
        let draggingComponent = WysiwygDom.getComponentWithId(components, draggingComponentId);

        if (WysiwygDom.isValidParentForComponent(activeDropTargetComponent, draggingComponent)) {
          const draggingComponentLineage = WysiwygDom.getComponentLineage(components, draggingComponent);
          const draggingComponentParent = (draggingComponentLineage[draggingComponentLineage.length - 2]);

          if (activeDropTargetComponent.get('id') !== draggingComponentParent.get('id')) {
            dropResult.isNewParent = true;

            const { innerOffsetX, innerOffsetY, offsetX, offsetY } = dragEvent;
            let newParentOffsetX = (offsetX - innerOffsetX) / scaleRatio;
            let newParentOffsetY = (offsetY - innerOffsetY) / scaleRatio;

            if (formType === FormTypes.WELCOME_MAT
              && draggingComponent.get('type') === 'background') {
              newParentOffsetX = 0;
              newParentOffsetY = 0;
            }

            draggingComponent = draggingComponent.mergeIn(
              ['style'],
              Immutable.Map({ top: `${newParentOffsetY}px`, left: `${newParentOffsetX}px` }),
            );

            // remove the component from it's old parent
            const keyPathForDraggingComponent = WysiwygDom.getKeyPathForComponent(
              components,
              draggingComponentId,
            );

            components = components.deleteIn(keyPathForDraggingComponent);

            // add the component to it's new parent
            const keyPathForDropTargetComponent = WysiwygDom.getKeyPathForComponent(
              components,
              activeDropTargetComponent,
            );
            let dropTargetChildComponents = activeDropTargetComponent.get('childComponents') || Immutable.List();

            dropTargetChildComponents = dropTargetChildComponents.push(draggingComponent);

            const restructuredComponents = components.setIn(
              keyPathForDropTargetComponent.concat(['childComponents']),
              dropTargetChildComponents,
            );

            onChangePopupJson({ components: restructuredComponents });
          }
        }
      } else if (id !== null) {
        // we're starting a drag, store the original parent
        const draggingComponentLineage = WysiwygDom.getComponentLineage(components, id);
        const draggingComponentParent = (draggingComponentLineage[draggingComponentLineage.length - 2]);

        newState.draggingComponentParent = draggingComponentParent;
      } else {
        // if we're not dragging a component anymore, let the component know if it's okay to drag
        dropResult.isValidMove = false;
      }

      this.setState(newState);

      return dropResult;
    }

    onResizeComponent(id) {
      const newState = {
        resizingComponentId: id,
        resizingParentComponent: undefined,
      };

      if (id !== null) {
        const { components } = this.props;

        // we're starting a resize, store the original parent
        const resizingComponentLineage = WysiwygDom.getComponentLineage(components, id);
        const resizingParentComponent = (resizingComponentLineage[resizingComponentLineage.length - 2]);

        newState.resizingParentComponent = resizingParentComponent;
      }

      this.setState(newState);
    }

    onFocusFieldComponent(id, blur) {
      const { isPreview } = this.props;
      if (isPreview || !id) return;

      // If we are blurring, unset the focused component.
      if (blur) return this.setState({ focusedFieldComponentId: null });
      this.setState({ focusedFieldComponentId: id });
    }

    // When is not a preview, if a field is focused, we ignore the escape key, but
    // if no field is focused, we close the popup on escape. When we are in a preview,
    // escape unselects components.
    onKeyUp(e) {
      const { isPreview, onClickAction, formType } = this.props;
      const { selectedComponentId, focusedFieldComponentId } = this.state;
      const keyCode = e.which || e.keyCode || 0;

      switch (keyCode) {
        case ESC: {
          if (isPreview || formType === FormTypes.INLINE_POPUP) {
            // Unselect component if selected
            if (selectedComponentId) this.onSelectComponent(null);
          } else {
            // Close popup if no focused field component
            if (onClickAction && !focusedFieldComponentId) onClickAction('close');
          }
          break;
        }
      }
    }

    getActiveDropTargetComponent () {
      const { components } = this.props;
      const { draggingComponentId } = this.state;
      let activeDropTargetComponent;

      if (draggingComponentId && this.hoveredComponentIds) {
        const draggingComponent = WysiwygDom.getComponentWithId(components, draggingComponentId);
        const orderedComponentIds = getOrderedComponentIds(this.hoveredComponentIds, components);

        if (orderedComponentIds) {
          orderedComponentIds.forEach((componentId) => {
            const component = WysiwygDom.getComponentWithId(components, componentId);

            if (WysiwygDom.isValidParentForComponent(component, draggingComponent)) {
              activeDropTargetComponent = component;
            }
          });
        }
      }

      return activeDropTargetComponent;
    }

    renderFontLinks() {
      const components = WysiwygDom.getDescendantComponents(this.props.components);
      const fontLinks = {};

      components.forEach(component => {
        const componentStyle = component.get('style');
        if (!componentStyle) return;

        const fontFamily = componentStyle.get('fontFamily');
        // If our font is a browser font, we don't need to render a link.
        if (!fontFamily || FontUtil.isBrowserFont(fontFamily)) return;

        // We use an object so we only have one font link per font family.
        const href = FontUtil.fullGoogleFontUrlFromFontFamily(fontFamily);
        if (href) {
          fontLinks[fontFamily] = (
            <link
              key={fontFamily}
              href={FontUtil.fullGoogleFontUrlFromFontFamily(fontFamily)}
              rel="stylesheet"
              type="text/css"
            />
          );
        }
      });

      return _.values(fontLinks);
    }

    getSnappedPosition(positionState) {
      const { id, width, height, formType } = this.props;
      const {
        isSnapping,
        isMoveLockedToAxis,
        resizeCorner,
        initialPosition,
        dragType,
      } = positionState;
      const components = WysiwygDom.getDescendantComponents(this.props.components);
      // getRoundedPosition copies the object so we don't need to.
      let position = getRoundedPosition(positionState);

      if (formType === FormTypes.SMART_BAR) return position;

      // Check if the alt key is held down.
      if (isSnapping) {
        const initialWidth = initialPosition.width;
        const initialHeight = initialPosition.height;
        const aspectRatio = initialWidth / initialHeight;
        let snapFunction;

        // Get the appropriate function to calculate snap positioning
        snapFunction = getSnapFunction(dragType);

        _.each(components, (component) => {
          if (!component) return;

          const componentId = component.get('id');
          const componentStyle = component.get('style');
          if (!componentStyle || componentId === id) return;

          position.aspectRatio = aspectRatio;

          // In all cases except corner resizing, this.resizeCorner is undefined.
          position = snapFunction(position, styleToPosition(componentStyle.toJS()), resizeCorner);
        });

        // Add aspect ratio for corner resizing.
        position.aspectRatio = aspectRatio;

        const popupPosition = { left: 0, top: 0, width: width, height: height };
        // In all cases except corner resizing, this.resizeCorner is undefined.
        position = snapFunction(position, popupPosition, resizeCorner);
      }

      // Snap disabling will not disable axis lock.
      // Calculate the deltas and decide which is bigger and we snap to that.
      if (isMoveLockedToAxis) {
        let nonSnappedPosition = getRoundedPosition(positionState);
        const {
          left: initialLeft,
          top: initialTop,
        } = initialPosition;
        // Use positionState so we get the original position and not snapped..
        const deltaX = nonSnappedPosition.left - initialLeft;
        const deltaY = nonSnappedPosition.top - initialTop;

        // Only modify the axis we are locking so snapping still applies.
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Lock to x axis
          position.top = initialTop;
        } else {
          // Lock to y axis
          position.left = initialLeft;
        }
      }

      return position;
    }

    buildState(props) {
      const {
        hoveredComponentId,
        selectedComponentId,
      } = props;

      return {
        selectedComponentId,
        hoveredComponentId,
      };
    }

    renderComponent(component, componentProps) {
      const {
        api,
        popupId,
        currentStep,
        isPreview,
        width,
        height,
        components,
        onChangeForm,
        onClickAction,
        onEnterSubmit,
        onChangeComponent,
        onDeleteComponent,
        formType,
        position,
        showPosition,
        showType,
        barSize,
        displayMode,
        defaultFormValues,
        formErrors,
        scrollBoxPosition,
        discountCode,
        discount,
        winningOption,
        onFinishGameAnimation,
        responsiveSize,
      } = this.props;

      const {
        selectedComponentId,
        draggingComponentId,
        hoveredComponentId,
        draggingComponentParent,
        activeDropTargetComponent,
        resizingParentComponent,
        resizingComponentId,
      } = this.state;

      const scaleRatio = componentProps && componentProps.scaleRatio ? componentProps.scaleRatio : this.props.scaleRatio;
      // Static + Bottom mode should disable autofocus (similar to embedded forms)
      const isStaticBottom = showType === 'static' && showPosition === 'bottom';
      const isEmbedded = (formType === FormTypes.INLINE_POPUP || (formType === FormTypes.WELCOME_MAT && ['embedded', 'parallax'].indexOf(displayMode) > -1) || isStaticBottom);
      const id = component.get('id');
      const name = component.get('name');
      const type = component.get('type');
      const componentJSON = component.toObject();
      const Component = componentForType(type);
      const selected = id === selectedComponentId;
      const hovered = id === hoveredComponentId;
      const draggingComponent = (draggingComponentId ? WysiwygDom.getComponentWithId(components, draggingComponentId) : undefined);
      const isValidDropTarget = draggingComponent && WysiwygDom.isValidParentForComponent(component, draggingComponent);
      const isActiveDropTarget = (activeDropTargetComponent ? activeDropTargetComponent.get('id') === id : false);
      const isDragComponentParent = (draggingComponentParent && draggingComponentParent.get('id') === id);
      const isDragComponent = (id === draggingComponentId);
      const isResizeComponentParent = (resizingParentComponent && resizingParentComponent.get('id') === id);
      const isResizeComponent = (id === resizingComponentId);
      const getSnappedFunction = (
        (formType === FormTypes.WELCOME_MAT && type === 'background')
        ? WysiwygSnap.noSnapFunction
        : this.getSnappedPosition
      );

      let hidesVerticalHandles = false;
      let hidesHorizontalHandles = false;
      let moveIsDisabled = false;
      let error;
      let defaultValue;
      let isFirstError;

      if (formType === FormTypes.SMART_BAR) {
        if (['background', 'text', 'countdown', 'checkbox', 'checkboxGroup', 'radio'].indexOf(type) > -1) {
          hidesVerticalHandles = true;
          hidesHorizontalHandles = true;
        } else if (type === 'button' || type === 'textField') {
          hidesVerticalHandles = true;
          hidesHorizontalHandles = false;
        }

        if (type === 'image' && component.get('footer') && position === 'bottom') {
          return null;
        }
      } else if (formType === Constants.FormTypes.WELCOME_MAT) {
        if (type === 'background' || type === 'gridRow' || type === 'gridColumn' || type === 'scaledStage') {
          hidesVerticalHandles = true;
          hidesHorizontalHandles = true;
          const style = component.get('style');

          if (type !== 'background') {
            moveIsDisabled = true;
          } else if (style) {
            const top = style.get('top');
            const left = style.get('left');

            if (
              top && top.indexOf('%') !== -1 ||
              left && left.indexOf('%') !== -1
            ) {
              moveIsDisabled = true;
            }
          }
        }
      }

      // If component is named, then it is a form component. If our form has
      // a value for the component name, then we pass it to the component.
      if (name) {
        const value = defaultFormValues.get(name);
        if (value) defaultValue = value;

        error = formErrors.get(name);
        if (!this.firstErrorFound && error) {
          isFirstError = true;
          this.firstErrorFound = true;
        }
      }

      // If component has a text prop, fill in any query params
      if (!isPreview && componentJSON.text && componentJSON.text.indexOf('{query:') > -1) {
        componentJSON.text = componentJSON.text.replace(/{query:([^{}]*)}/g, (a, b) => this.queryVars[b] || '');
      }

      if ((!discount || !discount.nextDiscount) && componentJSON.shortcutMeta && componentJSON.shortcutMeta.get('requiresDiscount')) {
        return null;
      }

      const discountProgress = discount ? discount.progressToNext : 0;

      let breakpointSizeToApply;
      // If responsiveSize is set, we need to apply the breakpoint overrides
      // Infinity is the default, no breakpoint overrides needed
      if (responsiveSize && responsiveSize !== Infinity) {
        // From state
        breakpointSizeToApply = responsiveSize;
      } else if (!responsiveSize) {
        // Client side rendering
        // Get the responsive size matching the window width
        const breakpointSizes = componentJSON?.breakpoints || {};
        breakpointSizeToApply = getResponsiveSize(breakpointSizes);
      }

      if (breakpointSizeToApply) {
        const finalStyle = composeBreakpointStyles(breakpointSizeToApply, componentJSON);
        if (finalStyle) {
          // eslint-disable-next-line no-param-reassign
          componentJSON.style = finalStyle;
        }
      }

      return (
        <Component
          key={id}
          currentStep={currentStep}
          isPreview={isPreview}
          api={api}
          popupId={popupId}
          popupScaleRatio={scaleRatio}
          onChangeForm={onChangeForm}
          onClickAction={onClickAction}
          onEnterSubmit={onEnterSubmit}
          selected={selected}
          hovered={hovered}
          isValidDropTarget={draggingComponentId && isValidDropTarget}
          isActiveDropTarget={isValidDropTarget && isActiveDropTarget}
          isDragComponentParent={isDragComponentParent}
          isDragComponent={isDragComponent}
          isResizeComponentParent={isResizeComponentParent}
          isResizeComponent={isResizeComponent}
          popupWidth={width}
          popupHeight={height}
          maxHeight={barSize}
          defaultValue={defaultValue}
          error={error}
          isFirstError={isFirstError}
          isEmbedded={isEmbedded}
          onChangeComponent={onChangeComponent}
          onDeleteComponent={onDeleteComponent}
          onSelectComponent={this.onSelectComponent}
          onMouseEnterComponent={this.onMouseEnterComponent}
          onMouseLeaveComponent={this.onMouseLeaveComponent}
          onMoveComponent={this.onDragComponent}
          onResizeComponent={this.onResizeComponent}
          onFocusFieldComponent={this.onFocusFieldComponent}
          hidesVerticalHandles={hidesVerticalHandles}
          hidesHorizontalHandles={hidesHorizontalHandles}
          moveIsDisabled={moveIsDisabled}
          formType={formType}
          position={position}
          showType={showType}
          getSnappedPosition={getSnappedFunction}
          renderComponent={this.renderComponent}
          scrollBoxPosition={scrollBoxPosition}
          discountCode={discountCode}
          discount={discount}
          winningOption={winningOption}
          disabled={!!winningOption && currentStep === 'subscribe' && !isPreview}
          onFinishGameAnimation={onFinishGameAnimation}
          onMouseDrag={this.props.onMouseDrag}
          componentDidMove={this.props.componentDidMove}
          changedJSON={this.props.changedJSON}
          progress={discountProgress}
          {...componentJSON}
          {...componentProps}
        />
      );
    }

    renderComponents() {
      const { components, formType } = this.props;
      let orderedComponents = components;

      this.firstErrorFound = false;

      if (formType === FormTypes.SMART_BAR) {
        // Move background components to back
        const backgroundComponents = components.filter(component => component.get('type') === 'background') || [];
        const inlineComponents = components.filter(component => component.get('type') !== 'background') || [];
        orderedComponents = backgroundComponents.concat(inlineComponents);
      }

      return orderedComponents
        .filter(component => componentForType(component.get('type')) !== undefined)
        .map(component => this.renderComponent(component, null));
    }

    renderPoweredByVariant() {
      const {
        api,
        onClickPoweredByVariant,
        isPreview,
        barSize,
        noSumoBranding,
      } = this.props;
      if (noSumoBranding || !api || !api.properties) return null;

      return (
        <a style={{ maxHeight: `${barSize}px` }} onClick={isPreview ? null : onClickPoweredByVariant} className="sumome-smartbar-powered-by-variant">
          <IsoLogoColor />
        </a>
      );
    }

    renderSmartBar() {
      const {
        isPreview,
        className,
        advancedForm,
        goal,
        position,
        showType,
        barSize,
      } = this.props;

      const isAdvancedForm = (goal === 'custom' && advancedForm);

      const popupClassName = classNames(className, {
        [POPUP_CLASS_NAME]: true,
        'sumome-smartbar-popup': true,
        'sumome-react-smartbar-advanced-form': isAdvancedForm,
        'sumome-react-smartbar-top': position === 'top',
        'sumome-react-smartbar-bottom': (position === 'bottom' && showType !== 'static'),
        [`sumome-react-smartbar-${showType}`]: !isPreview,
        'sumome-react-smartbar-preview': isPreview,
      });

      const style = {
        minHeight: `${barSize}px`,
      };

      if (showType === 'static' && position === 'bottom') {
        style.top = `${this.scrollHeight}px`;
      }

      if (showType === 'static') {
        const bodyStyles = window.getComputedStyle(document.body);
        let { paddingLeft, marginLeft } = bodyStyles;
        paddingLeft = parseInt(paddingLeft, 10);
        marginLeft = parseInt(marginLeft, 10);
        const buffer = (paddingLeft || 0) + (marginLeft || 0);
        style.marginLeft = `${buffer * -1}px`;
      }

      if (isAdvancedForm) {
        return (
          <div
            ref={(el) => { this.advancedForm = el; }}
            className={popupClassName}
            dangerouslySetInnerHTML={{ __html: `<div class="sumome-smartbar-content">${advancedForm}</div><a class="sumome-advanced-form-close"></a>` }}
          >
          </div>
        );
      }

      return (
        <div
          className={popupClassName}
          style={style}
          role="button"
          tabIndex={0}
          onMouseDown={this.onUnselectComponent}
          ref={(smartbar) => { this.form = smartbar; }}
        >
          {this.renderFontLinks()}
          {this.renderComponents()}
          {this.renderPoweredByVariant()}
        </div>
      );
    }

    renderNoDropZonesIfNeeded(isNoNo) {
      if (isNoNo === false) return null;

      const { components, breakpointSize } = this.props;
      const isVertical = WysiwygUtil.isMobileBreakpoint(breakpointSize);
      const responsiveSize = (isVertical ? 'small' : 'medium');
      const scaledStageComponents = WysiwygDom.getDescendantComponents(components)
        .filter(c => c.get('type') === 'scaledStage')
        .filter(c => (
          !c.has('responsiveHiddenOn') ||
          c.get('responsiveHiddenOn').get(responsiveSize) === false
        ));

      // split layouts don't need no no's
      if (scaledStageComponents.length === 1) {
        const dropZones = [];
        const scaledStage = scaledStageComponents[0];
        const lineage = WysiwygDom.getComponentLineage(components, scaledStage);
        const gridColumn = lineage[lineage.length - 2];
        const scaledStageStyle = scaledStage.get('style');
        const containerWidth = (isVertical ? 500 : 1000);
        const containerHeight = (isVertical ? 1000 : 500);
        const stageWidth = parseInt(scaledStageStyle.get('width'), 10);
        const stageHeight = parseInt(scaledStageStyle.get('height'), 10);
        const scaleRatio = Math.min(containerHeight / stageHeight, containerWidth / stageWidth);
        const width = (stageWidth * scaleRatio);
        const height = (stageHeight * scaleRatio);

        if (isVertical) {
          const remainder = (containerHeight - height) / 2;

          dropZones.push({ left: 0, top: 0, width: `${width}px`, height: `${remainder}px` });
          dropZones.push({ left: 0, bottom: 0, width: `${width}px`, height: `${remainder}px` });
        } else {
          let remainder = (containerWidth - width);
          const contentAlign = gridColumn.get('contentAlign');

          if (contentAlign !== 'center') {
            dropZones.push({
              left: (contentAlign === 'left' ? 'auto' : '0'),
              right: (contentAlign === 'right' ? 'auto' : '0'),
              top: 0,
              width: `${remainder}px`,
              height: `${height}px`,
            });
          } else {
            remainder /= 2;

            dropZones.push({ left: 0, top: 0, width: `${remainder}px`, height: `${height}px` });
            dropZones.push({ right: 0, top: 0, width: `${remainder}px`, height: `${height}px` });
          }
        }

        return (
          <div style={{ position: 'relative', height: '100%' }}>
            {dropZones.map((dropZone, i) => (
              <div
                className="sumome-react-wysiwyg-no-drop-zone"
                style={dropZone}
                key={i}
              />
            ))}
          </div>
        );
      }

      return null;
    }

    renderPopup() {
      const {
        width,
        isPreview,
        className,
        advancedForm,
        goal,
        size,
        components,
        api,
        noSumoBranding,
        showPosition,
        height,
        formType,
        scaleRatio,
      } = this.props;

      const { draggingComponentId, resizingComponentId, activeDropTargetComponent } = this.state;
      const isAdvancedForm = (goal === 'custom' && advancedForm);
      const draggingComponent = WysiwygDom.getComponentWithId(components, draggingComponentId);
      const currentActiveDropTargetComponent = activeDropTargetComponent || this.getActiveDropTargetComponent();
      const hasValidDropTarget = draggingComponent
        && currentActiveDropTargetComponent
        && WysiwygDom.isValidParentForComponent(currentActiveDropTargetComponent, draggingComponent);
      const popupClassName = classNames(className, {
        [POPUP_CLASS_NAME]: true,
        ['sumome-advanced-form']: isAdvancedForm,
        [`sumome-res-${size}`]: isAdvancedForm,
        [`sumome-react-wysiwyg-${formType}`]: true,
        'sumome-react-wysiwyg-is-dragging': (draggingComponentId !== null),
        'sumome-react-wysiwyg-is-resizing': (resizingComponentId !== null),
        'sumome-react-wysiwyg-has-valid-drop-target': (hasValidDropTarget === true),
        'sumome-react-wysiwyg-is-preview': isPreview,
      });

      // Show sumo badge if user has non-pro status
      let sumoBadge = null;

      // if (!noSumoBranding && formType !== FormTypes.WELCOME_MAT) {
      if (!noSumoBranding && formType !== FormTypes.WELCOME_MAT) {
        sumoBadge = (
          <SumoBadge
            api={api}
            isPreview={isPreview}
            showPosition={showPosition}
            height={height}
            formType={formType}
            scaleRatio={scaleRatio}
          />
        );
      }

      if (isAdvancedForm) {
        return (
          <div>
            <div
              ref={(el) => { this.advancedForm = el; }}
              className={popupClassName}
              dangerouslySetInnerHTML={{ __html: `<div>${advancedForm}</div><a class="sumome-advanced-form-close"></a>` }}
            />
            {sumoBadge}
          </div>
        );
      }

      const popupStyle = {
        width: `${width}px`,
        height: `${height}px`,
      };

      // onUnselectComponent is onMouseDown because we don't want to accidentally
      // unselect components when releasing the drag.
      return (
        <div
          className={popupClassName}
          onMouseDown={this.onUnselectComponent}
          style={popupStyle}
        >
          {this.renderFontLinks()}
          {this.renderComponents()}
          {this.renderNoDropZonesIfNeeded((draggingComponentId !== null) && !hasValidDropTarget)}
          {sumoBadge}
        </div>
      );
    }

    render() {
      const { formType } = this.props;

      switch (formType) {
        case FormTypes.SMART_BAR:
          return this.renderSmartBar();
        default:
          return this.renderPopup();
      }
    }
  }

  Popup.propTypes = {
    advancedForm: PropTypes.string.isRequired,
    animation: PropTypes.instanceOf(Object),
    api: PropTypes.instanceOf(Object).isRequired,
    className: PropTypes.string.isRequired,
    components: PropTypes.instanceOf(Object),
    defaultFormValues: PropTypes.instanceOf(Object),
    formErrors: PropTypes.instanceOf(Object),
    formType: PropTypes.string.isRequired,
    goal: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    isPreview: PropTypes.bool.isRequired,
    inDesignerMode: PropTypes.bool,
    noSumoBranding: PropTypes.bool.isRequired,
    onChangePopupJson: PropTypes.func.isRequired,
    onChangePopupSize: PropTypes.func.isRequired,
    onChangeSmartBarHeight: PropTypes.func.isRequired,
    onClickAction: PropTypes.func.isRequired,
    onClickPoweredByVariant: PropTypes.func.isRequired,
    onSelectComponent: PropTypes.func.isRequired,
    position: PropTypes.string.isRequired,
    breakpointSize: PropTypes.number.isRequired,
    responsiveSize: PropTypes.string.isRequired,
    scaleRatio: PropTypes.number.isRequired,
    showPosition: PropTypes.string.isRequired,
    showType: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    style: PropTypes.instanceOf(Object),
    updateSetting: PropTypes.func.isRequired,
    width: PropTypes.number.isRequired,
  };

  Popup.defaultProps = {
    animation: Immutable.Map(),
    components: Immutable.List(),
    defaultFormValues: Immutable.Map(),
    formErrors: Immutable.Map(),
    style: Immutable.Map(),
    inDesignerMode: false,
  };

  return ReactRedux.connect(
    (state, props) => ({
      noSumoBranding: (
        'noSumoBranding' in props
        ? props.noSumoBranding
        : state.appstore.getIn(['features', FEATURES.NO_SUMO_BRANDING], true)
      ),
    }),
    {
      updateSetting: AppActions.updateSetting,
    },
  )(Popup);
});
